import { parsePhoneNumber } from 'libphonenumber-js'
import { AuthUser } from '../models'
import store from '../store'
import axios from 'axios'
import trim from 'lodash-es/trim'
import { i18n } from '../localization'
import isEmpty from 'lodash-es/isEmpty'

// const phoneUtil = libphonenumber.PhoneNumberUtil.getInstance()
const URLRegex = new RegExp(/(^\{{2,3})(\w+)(.\w+)?(\}{2,3}$)|(^(http|https):\/\/[^ "]+$)/g)

const newURLRegex = new RegExp(
  /https?:\/\/(www\.)?[-a-zA-Z0-9@:%._\+~#=]{2,256}\.[a-z]{2,4}\b([-a-zA-Z0-9@:%_\+.~#?&//=]*)/
)

/*
usage of passing customMessage for all the validators
<Field name="email" :rules="{ email: ['Custom email error'] }" />
*/
// Phone validator
export const phoneValidator = (value: string, [customMessage]) => {
  if (!value) return true
  try {
    const phoneNumber = parsePhoneNumber(value)
    const isValid = phoneNumber.isValid()

    if (isValid) return true

    /**
     * If the phone number is not valid, then we need to bypass the validation for the following countries
     *
     * Special phone number handling for below countries
     *    Argentina -> +54 11 1234 5678, +54 9 11 1234 5678
     *    Brazil -> +55 11 1234 5678, +55 11 9 1234 5678
     *    Ivory Coast -> +225 xx 00 00 00 00 (10digits) or +225 00 00 00 00 (8digits)
     *    Mexico -> +52 1 55 1234 5678, +52 55 1234 5678
     *
     * These are obsolete formats for the phone number validation of these respective countries
     * We're only supporting them to cater to whatsapp's phone number format
     */
    const specialPhoneNumberPatterns = [
      /^(\+?52)?(?:1?[1-9]\d{9})$/, // Mexican numbers (10 digits, optional 1 prefix)
      /^(\+?225)?(?:0\d{9}|[2-7]\d{7})$/, // Ivory Coast (10 digit mobile or 8 digit landline)
      /^(\+?54)?(?:9?\d{10})$/, // Argentina (with or without 9 prefix)
      /^(\+?55)?\d{8,11}$/, // Brazil (8-11 digits)
    ]

    if (specialPhoneNumberPatterns.some(pattern => pattern.test(value))) {
      return true
    }

    return customMessage || i18n.global.t('common.validations.invalidPhone')
  } catch {
    return customMessage || i18n.global.t('common.validations.invalidPhone')
  }
}

// Strong password validator
export const strongPwdValidator = (value: string, [customMessage]) => {
  const strongRegex = new RegExp('^(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.*[!@#$%^&*;:"\'{}()_/?|~.,<>`-])(?=.{8,})')
  if (strongRegex.test(value)) return true

  return (
    customMessage ||
    'The password must be more than 8 characters including uppercase, lowercase, numbers and special characters.'
  )
}

// Unique email validator
export const uniqueEmailValidator = async (value: string, [originalValue, customMessage]) => {
  if (originalValue && originalValue.length > 0 && originalValue[0] === value) return true

  try {
    const auth: AuthUser = new AuthUser(await store.dispatch('auth/get'))
    const response = await axios.post('/exists', {
      email: value,
      companyId: auth.companyId,
    })

    if (response.status === 200) return true
    return customMessage || 'A user with this email already exists in this agency.'
  } catch {
    return customMessage || 'A user with this email already exists in this agency.'
  }
}

// Email or phone validator
export const emailOrPhoneValidator = (value: string, [customMessage]) => {
  if (!value) return true

  try {
    // Try phone validation first
    const phoneNumber = parsePhoneNumber(value)
    if (phoneNumber.isValid()) return true
  } catch {
    // Then try email validation
    const emailRegex = new RegExp(
      /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
    )
    if (emailRegex.test(String(value).toLowerCase())) return true
  }

  return customMessage || 'Invalid email address/phone number'
}

// Email validator
export const emailValidator = (value: string, [customMessage]) => {
  //this regex is taken from lib itself
  const emailRE = /^(?!\.)(?!.*\.\.)([A-Z0-9_'+\-\.]*)[A-Z0-9_+-]@([A-Z0-9][A-Z0-9\-]*\.)+[A-Z]{2,}$/i

  // Empty values should pass validation (unless required rule is also applied)
  if (isEmpty(value)) {
    return true
  }

  // Handle array values (for multiple emails)
  if (Array.isArray(value)) {
    const allValid = value.every(val => emailRE.test(String(val)))

    if (allValid) return true
    return customMessage || i18n.global.t('common.validations.invalidEmail')
  }

  // Validate single email address
  if (emailRE.test(String(value))) return true

  // Return custom message or translation
  return customMessage || i18n.global.t('common.validations.invalidEmail')
}

// Twilio phone validator
export const twilioPhoneValidator = (value: string, [phone_list, customMessage]) => {
  if (!phone_list?.includes(value)) return true
  return customMessage || 'You cannot use your inbound number here, it will create a call loop.'
}

// Twilio phone exclude hosted validator
export const twilioPhoneExcludeHostedValidator = (value: string, [numbers, customMessage]) => {
  if (numbers.phone_numbers?.includes(value)) {
    if (!numbers.hosted_numbers?.[value]) {
      return customMessage || 'You cannot use your inbound number here, it will create a call loop.'
    }
  }

  return true
}

// Handlebars validator
/*
example to use this validator
<Field name="url" :rules="{ required:true, handlebars: [true, 'Custom error message for handlebars validation'] }" />
<Field name="url" :rules="{ handlebars: [true, 'Custom error message for handlebars validation'] }" />
*/
export const handlebarsValidator = (value: string, [useJSSoup, customMessage]) => {
  if (!value) return true

  const regex =
    /((^{{|[^{\n\r]{{)(?!{))(?!( ?(\w\.?)+ ?(}}(?!}))))|((^{{{|[^{\n\r]{{{)(?!{))(?!( ?(\w\.?)+ ?(}}}(?!}))))|{{4,}|^{(?= )/m

  let isValid = true
  if (useJSSoup) {
    try {
      const parser = new DOMParser()
      const doc = parser.parseFromString(value, 'text/html')
      const testString = doc?.body?.textContent ?? doc?.textContent ?? value
      isValid = !regex.test(testString ?? '')
    } catch {
      isValid = !regex.test(value)
    }
  } else {
    isValid = !regex.test(value)
  }

  if (isValid) return true
  return customMessage || 'There are issues in your custom variables, please fix them before saving.'
}

// URL validator
export const urlValidator = (value: string, [customMessage]) => {
  /*
  By manually resetting lastIndex to 0 before each test, we ensure the regex always starts checking from the beginning of the string, giving consistent results regardless of previous matches.
  */
  // Reset regex lastIndex because it has the 'g' flag
  URLRegex.lastIndex = 0

  if (URLRegex.test(value)) return true
  return customMessage || 'Invalid url.'
}

// New URL validator
export const newUrlValidator = (value: string, [customMessage]) => {
  // Reset regex lastIndex because it has the 'g' flag
  newURLRegex.lastIndex = 0

  if (newURLRegex.test(value)) return true
  return customMessage || 'Invalid url'
}

// URL to empty validator
export const urlToEmptyValidator = (value: string, [customMessage]) => {
  if (value == '') return true

  // Reset regex lastIndex because it has the 'g' flag
  URLRegex.lastIndex = 0

  if (URLRegex.test(value)) return true
  return customMessage || 'Invalid url.'
}

// Not empty validator
export const notEmptyValidator = (value: string, [customMessage]) => {
  if (trim(value) !== '') return true
  return customMessage || 'Field must not be empty.'
}

// Required field validator
export const requiredField = (value: string, [customMessage], { field }) => {
  if (trim(value) !== '') return true
  return customMessage || i18n.global.t('common.validations.requiredField', { fieldName: field })
}

// Confirmed field validator (for password confirmation, etc.)
export const confirmedField = (value: string, [target]: [string], context: any) => {
  if (!value) return true // Let required rule handle empty values

  if (value === target) return true

  // If validation fails, return translated error message
  return i18n.global.t('campaign.regulatoryBundle.auth.changePassword.passwordMismatch')
}
